<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@chatwoot/pico-search';
import draggable from 'vuedraggable';

import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TaskGroupEditor from 'dashboard/modules/kanban/components/task_groups/TaskGroupEditor.vue';

const editorRef = ref(null);

const store = useStore();
const { t } = useI18n();

const groups = useMapGetter('kanbanTaskGroups/getGroups');
const uiFlags = useMapGetter('kanbanTaskGroups/getUIFlags');

const searchQuery = ref('');
const editingGroup = ref(null);
const isCreating = ref(false);
const deleteDialogRef = ref(null);
const deletingId = ref(null);
const groupToDelete = ref(null);

const isEditing = computed(
  () => isCreating.value || editingGroup.value !== null
);

const localGroups = ref([]);

watch(
  groups,
  val => {
    localGroups.value = val ? [...val] : [];
  },
  { immediate: true }
);

const filteredGroups = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return localGroups.value;
  return picoSearch(localGroups.value, query, ['name']);
});

async function onGroupDragEnd() {
  await store.dispatch(
    'kanbanTaskGroups/reorder',
    localGroups.value.map(g => g.id)
  );
}

function openCreate() {
  editingGroup.value = null;
  isCreating.value = true;
}

function openEdit(group) {
  isCreating.value = false;
  editingGroup.value = group;
}

function closeEditor() {
  editingGroup.value = null;
  isCreating.value = false;
}

function openDeleteDialog(group) {
  groupToDelete.value = group;
  deleteDialogRef.value?.open();
}

async function confirmDelete() {
  deletingId.value = groupToDelete.value?.id;
  try {
    await store.dispatch('kanbanTaskGroups/delete', groupToDelete.value.id);
    useAlert(t('KANBAN.TASK_GROUPS.DELETE.SUCCESS'));
  } catch {
    useAlert(t('KANBAN.TASK_GROUPS.DELETE.ERROR'));
  } finally {
    deletingId.value = null;
    groupToDelete.value = null;
  }
}

onMounted(() => {
  store.dispatch('kanbanTaskGroups/fetch');
  store.dispatch('agents/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags?.isFetching && !isEditing"
    :loading-message="t('KANBAN.TASK_GROUPS.LOADING')"
  >
    <template #header>
      <!-- Título e descrição — sempre visíveis -->
      <div class="flex flex-col items-start w-full mb-4">
        <h1 class="text-heading-1 text-n-slate-12 mb-2">
          {{ t('KANBAN.TASK_GROUPS.TAB') }}
        </h1>
        <p class="mb-0 max-w-3xl text-body-main text-n-slate-11">
          {{ t('KANBAN.TASK_GROUPS.DESCRIPTION') }}
        </p>
      </div>
      <!-- Barra de ação: modo edição -->
      <div
        v-if="isEditing"
        class="flex items-center justify-between w-full gap-4 min-h-8"
      >
        <div class="flex items-center gap-2 flex-1 min-w-0">
          <button
            class="flex items-center gap-1 text-sm text-n-slate-10 hover:text-n-slate-12 transition-colors flex-shrink-0"
            @click="closeEditor"
          >
            <span class="i-lucide-chevron-left size-4" />
          </button>
          <input
            v-if="editorRef"
            v-model="editorRef.name"
            type="text"
            :placeholder="t('KANBAN.TASK_GROUPS.NAME_PLACEHOLDER')"
            class="text-heading-1 text-n-slate-12 placeholder:text-n-slate-9 !bg-transparent !outline-none !shadow-none !border-none !p-0 !m-0 !h-auto !rounded-none flex-1 min-w-[120px] w-full"
          />
          <span v-else class="text-heading-1 text-n-slate-12">
            {{ editingGroup ? editingGroup.name : t('KANBAN.TASK_GROUPS.NEW') }}
          </span>
        </div>
        <div class="flex items-center gap-3">
          <span
            v-if="uiFlags?.isSaving"
            class="text-xs text-n-slate-10 flex items-center gap-1 flex-shrink-0"
          >
            <span class="i-lucide-loader animate-spin size-3" />
            {{ t('KANBAN.TASK_GROUPS.SAVING') }}
          </span>
          <button
            type="button"
            class="flex items-center gap-1 text-sm text-n-brand hover:opacity-80 font-medium shrink-0"
            @click="editorRef?.addItem()"
          >
            <span class="i-lucide-plus size-3.5" />
            {{ t('KANBAN.TASK_GROUPS.ADD_ITEM') }}
          </button>
        </div>
      </div>
      <!-- Barra de ação: modo listagem -->
      <div
        v-else
        class="flex items-center justify-between w-full gap-4 sm:mt-4"
      >
        <div class="flex items-center gap-3">
          <span v-if="groups?.length" class="text-body-main text-n-slate-11">
            {{ t('KANBAN.TASK_GROUPS.COUNT', { n: groups.length }) }}
          </span>
        </div>
        <div class="flex items-center gap-3 ml-auto">
          <input
            v-model="searchQuery"
            type="search"
            :placeholder="t('KANBAN.TASK_GROUPS.SEARCH_PLACEHOLDER')"
            class="group w-56 hidden sm:flex border border-n-weak rounded-[0.625rem] px-3 py-1.5 text-sm text-n-slate-12 bg-n-background outline-none focus:border-n-brand"
          />
          <Button
            :label="t('KANBAN.TASK_GROUPS.NEW')"
            size="sm"
            @click="openCreate"
          />
        </div>
      </div>
    </template>

    <template #body>
      <!-- Editor inline -->
      <div v-if="isEditing" class="py-2">
        <TaskGroupEditor
          ref="editorRef"
          :group="editingGroup"
          @saved="store.dispatch('kanbanTaskGroups/fetch')"
        />
      </div>

      <!-- Tabela de grupos -->
      <div v-else class="w-full">
        <p
          v-if="!filteredGroups.length"
          class="py-8 text-center text-sm text-n-slate-10"
        >
          {{
            searchQuery
              ? t('KANBAN.TASK_GROUPS.NO_RESULTS')
              : t('KANBAN.TASK_GROUPS.EMPTY')
          }}
        </p>
        <table v-else class="w-full">
          <draggable
            v-model="localGroups"
            item-key="id"
            tag="tbody"
            ghost-class="opacity-40"
            :disabled="!!searchQuery"
            @end="onGroupDragEnd"
          >
            <template #item="{ element: group }">
              <tr
                :key="group.id"
                class="border-b border-n-weak hover:bg-n-alpha-1 transition-colors cursor-grab active:cursor-grabbing"
                @click="openEdit(group)"
              >
                <td class="px-4 py-3 max-w-0">
                  <div class="flex items-center gap-3 min-w-0">
                    <span
                      class="i-lucide-list-checks size-5 text-n-slate-11 flex-shrink-0"
                    />
                    <span
                      class="text-body-main text-n-slate-12 truncate font-medium"
                    >
                      {{ group.name }}
                    </span>
                  </div>
                </td>
                <td class="px-4 py-3 w-40">
                  <span class="text-body-main text-n-slate-11">
                    {{
                      t('KANBAN.TASK_GROUPS.ITEMS_COUNT', {
                        count: group.items?.length ?? 0,
                      })
                    }}
                  </span>
                </td>
                <td class="px-4 py-3 w-24">
                  <div class="flex gap-3 justify-end flex-shrink-0" @click.stop>
                    <Button
                      v-tooltip.top="t('KANBAN.TASK_GROUPS.EDIT')"
                      icon="i-woot-edit-pen"
                      slate
                      sm
                      @click="openEdit(group)"
                    />
                    <Button
                      v-tooltip.top="t('KANBAN.TASK_GROUPS.DELETE.BUTTON')"
                      icon="i-woot-bin"
                      slate
                      sm
                      class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                      @click="openDeleteDialog(group)"
                    />
                  </div>
                </td>
              </tr>
            </template>
          </draggable>
        </table>
      </div>
    </template>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('KANBAN.TASK_GROUPS.DELETE.TITLE')"
      :description="
        t('KANBAN.TASK_GROUPS.DELETE.MESSAGE', {
          name: groupToDelete?.name ?? '',
        })
      "
      :is-loading="!!deletingId"
      :confirm-button-label="t('KANBAN.TASK_GROUPS.DELETE.CONFIRM')"
      :cancel-button-label="t('KANBAN.TASK_GROUPS.DELETE.CANCEL')"
      @confirm="confirmDelete"
    />
  </SettingsLayout>
</template>
